import React, { useMemo } from 'react';
import { GeographicCondition } from '../../../../types/qr.types';
import { Autocomplete, TextField, Chip, Box } from '@mui/material';
import { X } from 'lucide-react';
import countryRegionData from 'country-region-data/data.json';

interface GeographicConditionEditorProps {
  condition: GeographicCondition;
  onChange: (condition: GeographicCondition) => void;
  onRemove: () => void;
  isLocked?: boolean; // Whether this feature is locked due to subscription
}

interface Country {
  countryName: string;
  countryShortCode: string;
  regions: Array<{
    name: string;
    shortCode: string;
  }>;
}

interface CountryOption {
  label: string;
  code: string;
}

export function GeographicConditionEditor({ condition, onChange, onRemove, isLocked = false }: GeographicConditionEditorProps) {
  const countries = countryRegionData as Country[];
  
  // Prepare country options for autocomplete
  const countryOptions: CountryOption[] = useMemo(() => {
    return countries.map(c => ({
      label: `${c.countryShortCode} - ${c.countryName}`,
      code: c.countryShortCode
    }));
  }, [countries]);

  // Get selected country objects
  const selectedCountries = useMemo(() => {
    return condition.countries.map(code => {
      const country = countries.find(c => c.countryShortCode === code);
      return {
        label: country ? `${code} - ${country.countryName}` : code,
        code
      };
    });
  }, [condition.countries, countries]);

  const handleCountriesChange = (_event: any, newValue: CountryOption[]) => {
    const newCountries = newValue.map(v => v.code);
    onChange({ ...condition, countries: newCountries, regions: [] });
  };

  return (
    <div className={`border rounded-lg p-4 space-y-4 ${
      isLocked
        ? 'bg-gray-50/50 border-gray-200 opacity-70'
        : 'bg-[--clinqr-bg-subtle]/50 border-[--clinqr-border]/10'
    }`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={isLocked ? 'text-gray-400' : 'text-[--clinqr-primary]'}>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </span>
          <div className="flex items-center gap-2">
            <span className={`font-medium ${isLocked ? 'text-gray-500' : 'text-[--clinqr-text-primary]'}`}>Geographic</span>
            {isLocked && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-amber-100 text-amber-800 rounded-full font-medium">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16h4v2h-4zm0-5h4v4h-4zm0-5h4v4h-4z" />
                </svg>
                Premium
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="text-red-500 hover:text-red-700 p-1"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {isLocked && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
          <strong>Premium Feature:</strong> Geographic routing is available on Pro and Business plans. Upgrade your account to use location-based rules.
        </div>
      )}

      {/* Countries Section */}
      <div>
        <label className="block text-sm font-medium text-[--clinqr-text-secondary] mb-2">Countries</label>

        <Autocomplete
          multiple
          disabled={isLocked}
          options={countryOptions}
          value={selectedCountries}
          onChange={handleCountriesChange}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.code === value.code}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={isLocked ? "This feature requires a premium plan" : "Select countries..."}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: isLocked ? '#f3f4f6' : 'white',
                  fontSize: '0.875rem',
                  '& fieldset': {
                    borderColor: 'var(--clinqr-border)',
                    borderOpacity: 0.3,
                  },
                  '&:hover fieldset': {
                    borderColor: isLocked ? '#d1d5db' : 'var(--clinqr-primary)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: isLocked ? '#d1d5db' : 'var(--clinqr-primary)',
                    borderWidth: '1px',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#f3f4f6',
                  },
                },
              }}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.code}
                label={option.label}
                size="small"
                sx={{
                  backgroundColor: 'var(--clinqr-lime)',
                  color: 'var(--clinqr-navy)',
                  fontWeight: 500,
                  '& .MuiChip-deleteIcon': {
                    color: 'var(--clinqr-navy)',
                    '&:hover': {
                      color: 'var(--clinqr-navy)',
                      opacity: 0.7,
                    },
                  },
                }}
              />
            ))
          }
          sx={{
            '& .MuiAutocomplete-listbox': {
              fontSize: '0.875rem',
            },
            '& .MuiAutocomplete-option': {
              '&:hover': {
                backgroundColor: 'var(--clinqr-lime)',
                opacity: 0.2,
              },
              '&[aria-selected="true"]': {
                backgroundColor: 'var(--clinqr-lime)',
                opacity: 0.3,
              },
            },
          }}
        />

        {condition.countries.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <p className="text-xs text-[--clinqr-text-muted] mb-2">
              Selected ({condition.countries.length}) countries
            </p>
          </Box>
        )}
      </div>

    </div>
  );
}